import {
  TableColumnHeader,
  TableColumnInstance,
} from '@orobix/orobix-fe-components'
import i18n from 'i18next'

import { SortButton } from '../../../components/SortButton/SortButton'
import { DrugRegistry } from '../../../types/drugRegistry'
import { Filtering, FilteringValue } from '../../../types/filtering'
import { SortingDirection } from '../../../types/sorting'
import { formatPlanningDate } from '../../OptimizationProcesses/OptimizationProcessesList/useColumns'
import { useStyles } from './DrugRegistryList.styles'
import { DrugStatusEditor } from './DrugStatusEditor'
import { FilterByBoolean } from './FilterByBoolean'
import { FilterByRange } from './FilterByRange'
import { FilterByText } from './FilterByText'
import { DimensionFilterKey, FilterKey, NanFilterKey } from './useDrugRegistry'

type ColumnsKeys = keyof DrugRegistry
type Column = TableColumnInstance<DrugRegistry, ColumnsKeys>

type UseColumnsProps = {
  sorting: { [key in keyof Partial<DrugRegistry>]: SortingDirection }
  handleSort: (key: ColumnsKeys, direction: SortingDirection) => void
  filtering: Filtering<Record<FilterKey, string>>
  handleFilter: (key: FilterKey, value: FilteringValue) => void
}

// 0 and NaN (a blank Anagrafica cell, stored as null) are the geometry
// anomalies counted by the panel above the table.
export const isDimensionAnomaly = (value: number | null) =>
  value === null || value === 0

const dimensionColumn = (
  key: 'height' | 'width' | 'depth',
  minKey: DimensionFilterKey,
  maxKey: DimensionFilterKey,
  nanKey: NanFilterKey,
  title: string,
  { sorting, handleSort, filtering, handleFilter }: UseColumnsProps,
  anomalyClassName: string
): Column => ({
  id: key,
  header: (
    <TableColumnHeader
      title={title}
      sorting={
        <SortButton
          sort={sorting?.[key]}
          onSort={(direction) => handleSort(key, direction)}
        />
      }
      filtering={
        <FilterByRange
          id={key}
          minValue={filtering?.[minKey]}
          maxValue={filtering?.[maxKey]}
          onFilterMin={(value) => handleFilter(minKey, value)}
          onFilterMax={(value) => handleFilter(maxKey, value)}
          nan={{
            value: filtering?.[nanKey],
            onFilter: (value) => handleFilter(nanKey, value),
          }}
        />
      }
    />
  ),
  template: (row) => {
    const value = row[key] as number | null
    return (
      <span
        className={isDimensionAnomaly(value) ? anomalyClassName : undefined}
      >
        {value ?? 'NaN'}
      </span>
    )
  },
})

export const useColumns = (props: UseColumnsProps): Column[] => {
  const { sorting, handleSort, filtering, handleFilter } = props
  const classes = useStyles()

  const minsanColumn: Column = {
    id: 'minsan',
    header: (
      <TableColumnHeader
        title={i18n.t('Minsan')}
        sorting={
          <SortButton
            sort={sorting?.minsan}
            onSort={(direction) => handleSort('minsan', direction)}
          />
        }
        filtering={
          <FilterByText
            id="minsan"
            value={filtering?.minsanIcontains}
            onFilter={(value) => handleFilter('minsanIcontains', value)}
          />
        }
      />
    ),
    th: true,
    template: ({ minsan }) => minsan,
  }

  const descriptionColumn: Column = {
    id: 'description',
    header: (
      <TableColumnHeader
        title={i18n.t('Description')}
        filtering={
          <FilterByText
            id="description"
            value={filtering?.descriptionIcontains}
            onFilter={(value) => handleFilter('descriptionIcontains', value)}
          />
        }
      />
    ),
    template: ({ description }) => description ?? 'N/A',
  }

  // Combines "Macchinabile" and "Origine" into one compact icon column: the
  // primary signal is always visible, the secondary one (manual override)
  // only adds a badge for the exception. DrugStatusEditor wraps the icon in
  // a click-to-edit popover (single-drug alternative to the bulk-upload
  // CSV). Both filters stay available, stacked in this column's header.
  const statusColumn: Column = {
    id: 'machinable',
    header: (
      <TableColumnHeader
        title={i18n.t('Machinable')}
        filtering={
          <>
            <FilterByBoolean
              id="machinable"
              filter={filtering?.machinable}
              onFilter={(value) => handleFilter('machinable', value)}
              trueLabel={i18n.t('Machinable')}
              falseLabel={i18n.t('Not machinable')}
            />
            <FilterByBoolean
              id="manualOverride"
              filter={filtering?.manualOverride}
              onFilter={(value) => handleFilter('manualOverride', value)}
              trueLabel={i18n.t('Manual')}
              falseLabel={i18n.t('Automatic')}
            />
          </>
        }
      />
    ),
    template: ({
      minsan,
      machinable,
      manualOverride,
      manualOverrideBy,
      manualOverrideAt,
    }) => (
      <DrugStatusEditor
        minsan={minsan}
        machinable={machinable}
        manualOverride={manualOverride}
        manualOverrideBy={manualOverrideBy}
        manualOverrideAt={manualOverrideAt}
      />
    ),
  }

  const lastMachinedRepartoColumn: Column = {
    id: 'lastMachinedReparto',
    header: (
      <TableColumnHeader
        title={i18n.t('Last department')}
        filtering={
          <FilterByText
            id="lastMachinedReparto"
            value={filtering?.lastMachinedReparto}
            onFilter={(value) => handleFilter('lastMachinedReparto', value)}
          />
        }
      />
    ),
    template: ({ lastMachinedReparto }) => lastMachinedReparto ?? 'N/A',
  }

  const lastMachinedAtColumn: Column = {
    id: 'lastMachinedAt',
    header: (
      <TableColumnHeader
        title={i18n.t('Last machined on')}
        sorting={
          <SortButton
            sort={sorting?.lastMachinedAt}
            onSort={(direction) => handleSort('lastMachinedAt', direction)}
          />
        }
        filtering={
          <FilterByRange
            id="lastMachinedAt"
            type="date"
            minValue={filtering?.lastMachinedAtGte}
            maxValue={filtering?.lastMachinedAtLte}
            onFilterMin={(value) => handleFilter('lastMachinedAtGte', value)}
            onFilterMax={(value) => handleFilter('lastMachinedAtLte', value)}
          />
        }
      />
    ),
    // `lastMachinedAt` is a date-only backend field: `formatDateTime` would
    // print a meaningless "00:00" time for it, so it uses the date-only
    // formatter instead (same one `OptimizationProcesses` uses for its own
    // date-only `planningStart`/`planningEnd` fields).
    template: ({ lastMachinedAt }) =>
      lastMachinedAt ? formatPlanningDate(lastMachinedAt) : 'N/A',
  }

  const firstSeenAtColumn: Column = {
    id: 'firstSeenAt',
    header: (
      <TableColumnHeader
        title={i18n.t('First seen on')}
        sorting={
          <SortButton
            sort={sorting?.firstSeenAt}
            onSort={(direction) => handleSort('firstSeenAt', direction)}
          />
        }
        filtering={
          <FilterByRange
            id="firstSeenAt"
            type="date"
            minValue={filtering?.firstSeenAtGte}
            maxValue={filtering?.firstSeenAtLte}
            onFilterMin={(value) => handleFilter('firstSeenAtGte', value)}
            onFilterMax={(value) => handleFilter('firstSeenAtLte', value)}
          />
        }
      />
    ),
    // Always midnight of a real source date now (the Configurazione
    // sighting's date, or the Anagrafica snapshot's own filename date) —
    // never a wall-clock timestamp — so the date-only formatter applies
    // here too, same as `lastMachinedAt`.
    template: ({ firstSeenAt }) => formatPlanningDate(firstSeenAt),
  }

  return [
    minsanColumn,
    descriptionColumn,
    dimensionColumn(
      'height',
      'heightGte',
      'heightLte',
      'heightNan',
      i18n.t('Height'),
      props,
      classes.dimensionAnomaly
    ),
    dimensionColumn(
      'width',
      'widthGte',
      'widthLte',
      'widthNan',
      i18n.t('Width'),
      props,
      classes.dimensionAnomaly
    ),
    dimensionColumn(
      'depth',
      'depthGte',
      'depthLte',
      'depthNan',
      i18n.t('Depth'),
      props,
      classes.dimensionAnomaly
    ),
    statusColumn,
    lastMachinedRepartoColumn,
    lastMachinedAtColumn,
    firstSeenAtColumn,
  ]
}
